import { ChevronRight } from '../../components/ChevronRight'
import { ConfettiBurst, shade } from '../../components/ConfettiBurst'
import { weekLabel } from '../../lib/api/weekly'
import type { UnseenWeeklyResult, WeeklyResultGame } from '../../lib/api/userFlags'
import { useT } from '../../i18n'

/**
 * "Results are in" popup — shown once on the first app open after a
 * weekly the player participated in settles. Each row deep-links to
 * that week's results screen; any dismissal acknowledges everything
 * shown (the placement is right here, so it's been seen).
 *
 * Every game id the v2 results RPC can return has a row here. A miss
 * would throw on `meta.art` and take the app down at boot, which is why
 * the server grew a v2 rather than widening the RPC shipped clients call.
 */

type T = ReturnType<typeof useT>

// The three older games have their own translated title keys. The word
// games reuse the Weekly tab's 'WEEKLY {game}' pattern, so their rows read
// the same as their cards in every locale (game names stay English).
const GAME_META: Record<WeeklyResultGame, { name: (t: T) => string; art: string; accent: string }> = {
  colorful:     { name: t => t('WEEKLY COLORFUL'),                       art: '/games/colorful.webp',     accent: '#74705F' },
  three_step:   { name: t => t('WEEKLY THREE STEP'),                     art: '/games/three-step.webp',   accent: '#0EAE93' },
  ideatthat:    { name: t => t('Weekly I’d Eat That'),                   art: '/games/ideatthat.webp',    accent: '#B58615' },
  fake_out:     { name: t => t('WEEKLY {game}', { game: 'FAKE OUT' }),     art: '/games/fake-out.webp',     accent: '#6b8a34' },
  close_enough: { name: t => t('WEEKLY {game}', { game: 'CLOSE ENOUGH' }), art: '/games/close-enough.webp', accent: '#a8a020' },
}

function ordinal(n: number): string {
  const rem100 = n % 100
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1: return `${n}st`
    case 2: return `${n}nd`
    case 3: return `${n}rd`
    default: return `${n}th`
  }
}

function scoreLine(r: UnseenWeeklyResult, t: T): string {
  if (r.game === 'colorful') {
    return t('Matched the crowd on {n} of 6', { n: r.my_score })
  }
  if (r.game === 'fake_out' || r.game === 'close_enough') {
    // The week's summed daily score, not votes: nobody votes on a daily.
    return t('{n} points', { n: r.my_score.toLocaleString() })
  }
  return r.my_score === 1
    ? t('1 vote from the community')
    : t('{n} votes from the community', { n: r.my_score })
}

export function WeeklyResultsPopup({
  results, onSeeResults, onDismiss,
}: {
  results: UnseenWeeklyResult[]
  onSeeResults: (r: UnseenWeeklyResult) => void
  onDismiss: () => void
}) {
  const t = useT()
  // A won trophy gets confetti in that game's color; several wins mix
  // their colors in one volley. Non-winning results stay quiet.
  const wonAccents = [...new Set(results.filter(r => r.is_winner).map(r => GAME_META[r.game].accent))]
  const confettiPalette = wonAccents.flatMap(a => [a, shade(a, 0.35), shade(a, -0.2)])
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6"
         role="dialog" aria-modal="true" aria-label={t('Weekly results are in')}>
      {confettiPalette.length > 0 && <ConfettiBurst palette={confettiPalette} zIndex={60} />}
      <div className="w-full max-w-sm rounded-3xl border-2 border-brand-border bg-white
                      shadow-card p-6 max-h-[80vh] overflow-y-auto">
        <p className="text-[10px] font-semibold text-brand-lime-dark uppercase tracking-widest">{t('While you were away')}</p>
        <h2 className="font-display text-xl font-bold text-brand-text mb-4">{t('Weekly results are in')}</h2>

        <div className="space-y-3 mb-5">
          {results.map(r => {
            const meta = GAME_META[r.game]
            return (
              <button
                key={`${r.game}:${r.event_id}`}
                onClick={() => onSeeResults(r)}
                className="w-full card p-4 flex items-center gap-3 text-left
                           active:bg-brand-elevated transition-colors"
              >
                <img src={meta.art} alt="" className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-widest"
                     style={{ color: meta.accent }}>
                    {meta.name(t)} · {weekLabel(r.week_start)}
                  </p>
                  <p className="font-semibold text-brand-text">
                    {r.is_winner
                      ? t('You won the week! 🏆')
                      : t('You placed {rank} of {total}', {
                          rank: ordinal(r.my_rank), total: r.participant_count,
                        })}
                  </p>
                  <p className="text-xs text-brand-muted">
                    {scoreLine(r, t)}
                  </p>
                </div>
                <ChevronRight className="shrink-0" />
              </button>
            )
          })}
        </div>

        <button onClick={onDismiss}
                className="w-full py-3 text-center text-sm font-medium text-brand-muted">
          Dismiss
        </button>
      </div>
    </div>
  )
}
